import { getDefaultPathForRole } from '@/auth/redirects';
import { useAuth } from '@/auth/useAuth';
import { Spinner } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import type { UserRole } from '@/types/types';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
// Se completa en el build de deploy.yml con el nombre de la versión; por defecto 'v1.0.0'.
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'v1.0.0';

function getIdTokenFromHash(): string | null {
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  return params.get('id_token');
}

export function LoginPage() {
  const { login, isAuthenticated, user, localLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(() => !!getIdTokenFromHash());
  const [error, setError] = useState<string | null>(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const idToken = getIdTokenFromHash();
    if (idToken) {
      window.history.replaceState({}, '', window.location.pathname);
      login(idToken)
        .catch((error) => {
          if (error instanceof Error) {
            setError(`Error al iniciar sesión: ${error.message}.`);
          } else {
            setError('Error al iniciar sesión. Por favor, intenta de nuevo.');
          }
          setIsErrorModalOpen(true);
        })
        .finally(() => setIsLoading(false));
    }
  }, [login]);

  useEffect(() => {
    if (GOOGLE_CLIENT_ID != '1046560782439-4vslnfuhq0jal3lmi4e347tgsfm5mpo7.apps.googleusercontent.com') {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === '.') {
        e.preventDefault();
        setVisible((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAuthenticated]);

  if (isAuthenticated) {
    const role = user?.asesor?.rol as UserRole | undefined;
    return <Navigate to={getDefaultPathForRole(role)} replace />;
  }

  const googleLogin = () => {
    if (visible) {
      setModalOpen(true);
      return;
    }
    const redirectUri = `${window.location.origin}/login`;
    const scope = 'openid email profile';
    const responseType = 'id_token';
    const nonce = crypto.randomUUID();

    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.set('client_id', GOOGLE_CLIENT_ID);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('response_type', responseType);
    url.searchParams.set('scope', scope);
    url.searchParams.set('nonce', nonce);

    window.location.href = url.toString();
  };

  if (isLoading) {
    return (
      <div className="grid place-items-center h-screen bg-white">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <span className="text-gray-400 text-sm tracking-widest uppercase">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>

      <div className="min-h-screen flex flex-col bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        {/* Nav */}
        <nav className="flex items-center justify-between px-8 py-5" style={{ borderBottom: '0.5px solid #e8e4e0' }}>
          <a href="https://vivexia.co/" target="_blank" rel="noopener noreferrer">
            <img src="/logo.webp" alt="VivexIA" className="h-8" />
          </a>
          <span className="text-xs tracking-[0.14em] uppercase" style={{ color: '#b0a898' }}>
            SIG · Turismo de Aventura
          </span>
        </nav>

        {/* Main grid */}
        <div className="flex-1 flex flex-col md:grid md:grid-cols-2">
          {/* Left — form side */}
          <div className="flex flex-1 flex-col justify-center px-10 md:px-16 py-16" style={{ borderRight: '0.5px solid #e8e4e0' }}>
            <p className="text-xs tracking-[0.18em] uppercase mb-5" style={{ color: '#b0a898' }}>
              Sistema Integrado de Gestión
            </p>
            <h1
              className="mb-4 font-light leading-[1.18]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(32px, 4vw, 44px)',
                color: '#1a1816',
              }}
            >
              Agencias de Viaje
              <br />
              de turismo de aventura
            </h1>

            <p className="text-sm leading-relaxed mb-4 max-w-xs" style={{ color: '#a09488' }}>
              Accede al sistema aquí
            </p>
            <div className="flex items-center gap-6">
              <img src="/ai_hand.png" alt="" className="w-10 opacity-60 rotate-270" style={{ filter: 'sepia(0.15) brightness(0.9)' }} />
              <button
                type="button"
                onClick={googleLogin}
                className="flex items-center gap-3 rounded-lg w-fit transition-shadow h-fit"
                style={{
                  background: '#fff',
                  color: '#2a2520',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: "'DM Sans', sans-serif",
                  border: '1px solid #dbd5ce',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 3px 10px rgba(0,0,0,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {visible ? 'Continuar con Email' : 'Continuar con Google'}
              </button>
              <img src="/ai_hand.png" alt="" className="w-10 opacity-60 rotate-90" style={{ filter: 'sepia(0.15) brightness(0.9)' }} />
            </div>
          </div>

          {/* Right — visual side */}
          <div className="h-full md:h-auto md:flex flex-col items-center justify-center" style={{ background: '#faf8f6' }}>
            {/* Institution logos */}
            <div className="flex items-end justify-center py-4 md:items-center gap-5 flex-wrap md:mt-9 md:pt-6 h-full">
              <a
                href="https://www.mincit.gov.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <img src="/min_industria_turismo.webp" alt="Ministerio de Industria y Turismo" className="h-7" />
              </a>
              <a
                href="https://fontur.com.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <img src="/fontur.webp" alt="Fontur" className="h-7" />
              </a>
              <a
                href="https://camaraarmenia.org.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="opacity-50 hover:opacity-100 transition-opacity"
              >
                <img src="/logo_camara.webp" alt="Cámara de Comercio" className="h-7" />
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-8 py-3" style={{ borderTop: '0.5px solid #e8e4e0' }}>
          <span className="text-[10px] tracking-[0.14em] uppercase" style={{ color: '#c4bcb0' }}>
            VIVEXIA · {APP_VERSION}
          </span>
        </div>

        {/* Modals — unchanged logic */}
        <Modal isOpen={isErrorModalOpen} onClose={() => setIsErrorModalOpen(false)} title="Error de Inicio de Sesión">
          <p className="text-gray-700">{error}</p>
        </Modal>
        <LocalLoginModal open={modalOpen} onClose={() => {}} localLogin={localLogin} />
      </div>
    </>
  );
}

/**
 * Modal para hacer login con el email
 * Accessible solo para SYSADMIN cuando no hay impersonación activa
 */

import { Button, Input } from '@/components/ui';
import { AlertCircle } from 'lucide-react';

interface LocalLoginModalProps {
  open: boolean;
  onClose: () => void;
  localLogin: (email: string) => Promise<void>;
}

export function LocalLoginModal({ open, onClose, localLogin }: LocalLoginModalProps) {
  const [search, setEmail] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const login = await localLogin(search);
      onClose();
      // Redirigir al dashboard del asesor
      window.location.replace('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar impersonación');
      console.error('Error al iniciar impersonación:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Iniciar sesión" size="md">
      <div className="space-y-4">
        {/* Error message */}
        {error && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Input placeholder="Escribir correo..." value={search} onChange={(e) => setEmail(e.target.value)} className="pl-4" />
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleLogin();
            }}
            disabled={loading}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            {loading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                Iniciando...
              </>
            ) : (
              'Iniciar sesión'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
